//
/* GS://PROTOCOLO
 * Arquivo: src/components/pos/ProductGrid.js
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 1.2 - Alerta Visual de Estoque e Liberação de Clique
 * Impacto da Versão: [src/components/pos/ProductGrid.js]
 * Data: 09/12/2025
 * Responsável: Gemini
 *
 * Mudanças desta versão:
 * - REMOVIDO: Trava `!isOutOfStock` no onClick (agora permite clicar em produtos zerados).
 * - ALTERADO: Estilo visual para produtos sem estoque. Em vez de `opacity-40`, usa `border-red-600`.
 * - ADICIONADO: Borda vermelha piscante (opcional, aqui fixa) ou sólida para destacar "Sem Estoque".
 */
//

import React, { useState, useEffect } from 'react';
import { Loader2, Package, Search } from 'lucide-react';

const ProductCard = ({ product, onAddToCart }) => {
    const isOutOfStock = !product.stock || product.stock <= 0;
    
    return (
        <div 
            // [GS-CORRECAO-INICIO: Permitir clique mesmo sem estoque]
            onClick={() => onAddToCart(product)}
            // [GS-CORRECAO-FIM]
            className={`relative p-2 bg-gray-800 rounded-lg shadow-md flex flex-col justify-between transition-transform transform hover:-translate-y-1 
                ${isOutOfStock 
                    ? 'border-2 border-red-600 hover:bg-gray-700 cursor-pointer' // Borda vermelha e cursor ativo
                    : 'cursor-pointer hover:bg-gray-700 border-2 border-transparent' // Borda transparente padrão
                }`}
        >
            <div className="relative w-full h-28 mb-2 rounded-md overflow-hidden bg-gray-900">
                {product.images && product.images[0] ? (
                    <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-10 h-10 text-gray-600" />
                    </div>
                )}
                {/* Badge de Estoque */}
                <span className={`absolute top-1 right-1 text-xs font-bold px-2 py-0.5 rounded-full ${isOutOfStock ? 'bg-red-600' : 'bg-green-600'} text-white shadow-sm`}>
                    {isOutOfStock ? `Esgotado (${product.stock || 0})` : `Estoque: ${product.stock}`}
                </span>
            </div>
            <div className="flex-grow">
                <h3 className="font-semibold text-sm text-white leading-tight">{product.name}</h3>
            </div>
            <p className="text-lg font-bold text-brand-gold mt-1">R$ {(product.price || 0).toFixed(2).replace('.', ',')}</p>
        </div>
    );
};

const ProductGrid = ({ products, onAddToCart, isLoading }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredProducts, setFilteredProducts] = useState([]);

    useEffect(() => {
        setFilteredProducts(products);
    }, [products]);

    useEffect(() => {
        if (!searchTerm) {
            setFilteredProducts(products);
            return;
        }
        const lowercasedFilter = searchTerm.toLowerCase();
        const filtered = products.filter(product =>
            (product.name && product.name.toLowerCase().includes(lowercasedFilter)) ||
            (product.sku && product.sku.toLowerCase().includes(lowercasedFilter))
        );
        setFilteredProducts(filtered);
    }, [searchTerm, products]);

    return (
        <div className="lg:col-span-2 flex flex-col bg-brand-dark rounded-lg p-4 h-full overflow-hidden">
            <h2 className="text-xl font-bold text-white mb-4">Produtos</h2>
            <div className="relative mb-4 flex-shrink-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                    type="text" placeholder="Procurar por nome ou SKU..." value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-gray-800 border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white focus:ring-2 focus:ring-brand-gold focus:outline-none"
                />
            </div>
            {isLoading ? (
                <div className="flex-grow flex items-center justify-center">
                    <Loader2 className="w-10 h-10 animate-spin text-brand-gold" />
                </div>
            ) : (
                <div className="flex-grow overflow-y-auto pr-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {filteredProducts.map(product => (
                        <ProductCard key={product.id} product={product} onAddToCart={onAddToCart} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default ProductGrid;